import React from "react";
import { ClockMini } from "../icons/ClockMini";
import { AnswerBox } from "./AnswerBox";
import { LocalRecommendations } from "./LocalRecommendations";
import { NavigationHelp } from "./NavigationHelp";
import { Card } from "../ui/Card";
import { SpyJournalModal } from "./SpyJournalModal";
import { colors, radius, spacing, transitions } from "../../utils/designSystem";
import { RECOMMENDATIONS_BY_STEP } from "../../data/tours/sanJuanTourData";

// Localized strings
const STRINGS = {
  en: {
    tabs: { directions: "Directions", riddle: "Riddle", history: "History", nearby: "Nearby", intel: "Intel", cipher: "Cipher" },
    headers: { directions: "Directions", riddle: "Cofresi's Clue", history: "History", nearby: "Nearby Recommendations", intel: "Intel", cipher: "Cipher" },
    walkTime: "min walk",
    needHelp: "Need help?",
    arrived: "I've arrived",
    learnAbout: "Learn about this location",
    clickForHint: "Click for a Hint",
    hintUnlocked: "Hint Unlocked",
    hintWait: "Hint will be available after 1 minute. Try to solve the riddle first!",
    transmission: "Transmission: Mission HQ",
    readIntel: "Read Intel",
  },
  es: {
    tabs: { directions: "Direcciones", riddle: "Acertijo", history: "Historia", nearby: "Cerca", intel: "Intel", cipher: "Cifrado" },
    headers: { directions: "Direcciones", riddle: "Pista de Cofresí", history: "Historia", nearby: "Lugares Cercanos", intel: "Intel", cipher: "Cifrado" },
    walkTime: "min caminando",
    needHelp: "¿Necesitas ayuda?",
    arrived: "He llegado",
    learnAbout: "Conoce este lugar",
    clickForHint: "Toca para una Pista",
    hintUnlocked: "Pista Desbloqueada",
    hintWait: "La pista estará disponible después de 1 minuto. ¡Intenta resolver el acertijo primero!",
    transmission: "Transmisión: Cuartel General",
    readIntel: "Leer Intel",
  }
};

export function ClueCard({ step, isRevealed, reveal, isCorrect, onSubmit, letterMask, onRevealLetter, isMissionMode = false, targetCoordinates = null, badgeColor = '#10B981', tour = null, progressHook = null, isFinalStep = false, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const [index, setIndex] = React.useState(0);
  const [dragX, setDragX] = React.useState(0);
  const [justUnlocked, setJustUnlocked] = React.useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = React.useState(false);
  const [audioDuration, setAudioDuration] = React.useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = React.useState(0);
  const [typedHint, setTypedHint] = React.useState('');
  const startXRef = React.useRef(null);
  const audioRef = React.useRef(null);
  const historyTextRef = React.useRef(null);
  const lastTypedHintRef = React.useRef(null);
  const typingIntervalRef = React.useRef(null);

  // Reset to Directions tab when step changes
  React.useEffect(() => {
    setIndex(0);
  }, [step?.id]);

  // Check if this step has nearby recommendations
  const hasNearbyRecommendations = React.useMemo(() => {
    if (isMissionMode) return false;
    const recommendations = (step && step.id && RECOMMENDATIONS_BY_STEP[step.id]) || [];
    return recommendations.length > 0;
  }, [step, isMissionMode]);

  // Tab titles change based on mode and whether recommendations exist
  const TITLES = React.useMemo(() => {
    if (isMissionMode) {
      return [t.tabs.directions, t.tabs.intel, t.tabs.cipher];
    }
    // Only include "Nearby" tab if recommendations exist
    if (hasNearbyRecommendations) {
      return [t.tabs.directions, t.tabs.riddle, t.tabs.history, t.tabs.nearby];
    }
    return [t.tabs.directions, t.tabs.riddle, t.tabs.history];
  }, [isMissionMode, hasNearbyRecommendations, t]);

  // Set hint unlock timer when user first views the Riddle/Encrypted section
  // For mission mode: index 2 (Encrypted), for regular mode: index 1 (Riddle)
  React.useEffect(() => {
    const riddleIndex = isMissionMode ? 2 : 1;
    if (index === riddleIndex && !isRevealed(step.id, "hint")) {
      if (typeof window !== 'undefined') {
        window.__hintUnlockTimes = window.__hintUnlockTimes || {};
        // Only set if not already set for this step
        if (!window.__hintUnlockTimes[step.id]) {
          window.__hintUnlockTimes[step.id] = Date.now() + 60000; // 60 seconds
        }
      }
    }
  }, [index, step.id, isRevealed, isMissionMode]);

  const maxIndex = TITLES.length - 1; // Dynamic based on number of tabs
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const handleStart = (clientX) => { startXRef.current = clientX; setDragX(0); };
  const handleMove = (clientX) => { if (startXRef.current == null) return; setDragX(clientX - startXRef.current); };
  const handleEnd = () => {
    if (startXRef.current == null) return;
    const dx = dragX;
    const threshold = 60;
    if (dx <= -threshold) setIndex((i) => clamp(i + 1, 0, maxIndex));
    else if (dx >= threshold) setIndex((i) => clamp(i - 1, 0, maxIndex));
    startXRef.current = null;
    setDragX(0);
  };

  const onTouchStart = (e) => handleStart(e.touches[0].clientX);
  const onTouchMove  = (e) => handleMove(e.touches[0].clientX);
  const onTouchEnd   = () => handleEnd();
  const onMouseDown  = (e) => { if (e.button !== 0) return; handleStart(e.clientX); };
  const onMouseMove  = (e) => { if (startXRef.current == null) return; handleMove(e.clientX); };
  const onMouseUp    = () => handleEnd();
  const onMouseLeave = () => handleEnd();

  // Audio control functions
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play().catch((err) => {
        console.error('Audio playback failed:', err);
        // User might need to interact with page first for autoplay
      });
      setIsAudioPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
    setAudioCurrentTime(0);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setAudioCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * audioDuration;
    audioRef.current.currentTime = newTime;
    setAudioCurrentTime(newTime);
  };

  // Reset audio when navigating away from Intel/History section
  React.useEffect(() => {
    const audioTabIndex = isMissionMode ? 1 : 2; // Intel tab in mission mode, History in regular mode
    if (index !== audioTabIndex && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsAudioPlaying(false);
      setAudioCurrentTime(0);
    }
  }, [index, isMissionMode]);

  // Auto-scroll text with audio playback
  React.useEffect(() => {
    if (isAudioPlaying && historyTextRef.current && audioDuration > 0) {
      const progress = audioCurrentTime / audioDuration;
      const scrollHeight = historyTextRef.current.scrollHeight - historyTextRef.current.clientHeight;
      historyTextRef.current.scrollTop = scrollHeight * progress;
    }
  }, [isAudioPlaying, audioCurrentTime, audioDuration]);

  // Typing animation for hint text
  React.useEffect(() => {
    const hintRevealed = isRevealed(step.id, "hint");
    const hintKey = `${step.id}-hint`;

    // Clear any existing typing animation
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    if (hintRevealed && step.hint) {
      // Check if we've already fully typed this hint
      if (lastTypedHintRef.current === hintKey) {
        // Already typed, show full hint immediately
        setTypedHint(step.hint);
        return;
      }

      console.log('[ClueCard] Starting hint typing animation for:', step.id, 'Length:', step.hint.length);
      lastTypedHintRef.current = hintKey;
      setTypedHint(''); // Reset

      const fullHint = step.hint;
      let currentIndex = 0;

      typingIntervalRef.current = setInterval(() => {
        currentIndex++;

        if (currentIndex <= fullHint.length) {
          setTypedHint(fullHint.substring(0, currentIndex));
          console.log('[ClueCard] Typing progress:', currentIndex, '/', fullHint.length);
        } else {
          console.log('[ClueCard] Typing complete');
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
      }, 20); // Type each character every 20ms
    } else if (!hintRevealed) {
      setTypedHint('');
    }

    return () => {
      if (typingIntervalRef.current) {
        console.log('[ClueCard] Cleanup: clearing typing interval');
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    };
  }, [isRevealed, step.id, step.hint]);

  const [showNavigationHelp, setShowNavigationHelp] = React.useState(false);
  const walkTimeLabel = React.useMemo(() => {
    const wt = step?.walkTimeMinutes ?? step?.walkTime ?? step?.walktime;
    if (wt == null || wt === "") return null;
    return typeof wt === "number" ? `${wt} ${t.walkTime}` : String(wt);
  }, [step, t]);

  // Convert hex color to rgba for inactive badge
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div
      className="relative"
      role="region"
      aria-label="Step content"
    >
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes unlockPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        @keyframes treasureReveal {
          0% { opacity: 0; transform: scale(0.9) translateY(10px); }
          50% { transform: scale(1.02) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .shimmer-button {
          background: linear-gradient(
            90deg,
            ${colors.treasure.gold} 0%,
            ${colors.treasure.amber} 25%,
            ${colors.neutral.white} 50%,
            ${colors.treasure.amber} 75%,
            ${colors.treasure.gold} 100%
          );
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }
        .unlock-pulse {
          animation: unlockPulse 0.4s ease-out;
        }
        .treasure-reveal {
          animation: treasureReveal 0.5s ease-out;
        }
        .slide-in-content {
          animation: slideInContent 0.3s ease-out;
        }
        @keyframes slideInContent {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Tab Navigation */}
      <div className="mb-4">
        <div
          className="flex gap-0.5 sm:gap-1 p-1"
          style={{
            background: colors.neutral[100],
            borderRadius: radius.lg,
            border: `1px solid ${colors.neutral[200]}`,
          }}
        >
          {TITLES.map((label, idx) => (
            <button
              key={label}
              onClick={() => setIndex(idx)}
              className="flex-1 text-xs sm:text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1 sm:gap-1.5"
              style={{
                borderRadius: radius.md,
                paddingTop: spacing[2],
                paddingBottom: spacing[2],
                paddingLeft: TITLES.length > 3 ? spacing[1.5] : spacing[3],
                paddingRight: TITLES.length > 3 ? spacing[1.5] : spacing[3],
                background: idx === index ? colors.neutral.white : 'transparent',
                color: idx === index ? colors.neutral[900] : colors.neutral[600],
                boxShadow: idx === index ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0, // Allow flex items to shrink below content size
                minHeight: '36px', // Ensure consistent height
              }}
              aria-current={idx === index ? 'page' : undefined}
            >
              <span
                className="flex items-center justify-center text-[9px] font-bold"
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: idx === index ? badgeColor : hexToRgba(badgeColor, 0.3),
                  color: colors.neutral.white,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </span>
              <span
                style={{
                  lineHeight: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
      <div
        className="flex will-change-transform"
        style={{
          transform: `translateX(${ -index * 100 }%) translateX(${dragX}px)`,
          transition: startXRef.current ? "none" : "transform 300ms ease-out",
        }}
      >
        {/* Each slide uses same wrapper as main card: max-w-xl mx-auto */}
        {TITLES.map((label, idx) => (
          <div key={label} className="min-w-full">
            <div className="max-w-xl mx-auto w-full">
              <Card className="glass-card p-6 md:p-7">
                {/* Section content */}
                {idx === 0 && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold">{t.headers.directions}</h3>
                      {walkTimeLabel && (
                        <span
                          className="text-xs font-medium flex items-center gap-1"
                          style={{
                            borderRadius: radius.full,
                            background: colors.neutral[100],
                            color: colors.neutral[600],
                            padding: `${spacing[1]} ${spacing[3]}`,
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          <ClockMini className="text-neutral-600" />
                          <span>{walkTimeLabel}</span>
                        </span>
                      )}
                    </div>
                    <div className="prose prose-sm max-w-none text-slate-800 break-words">
                      <div className="whitespace-normal" dangerouslySetInnerHTML={{ __html: step.directions }} />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowNavigationHelp(true)}
                        className="text-xs font-medium"
                        style={{
                          borderRadius: radius.full,
                          background: colors.neutral[100],
                          color: colors.neutral[600],
                          padding: `${spacing[1]} ${spacing[3]}`,
                          border: `1px solid ${colors.neutral[200]}`,
                          transition: transitions.default,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = colors.neutral[200];
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = colors.neutral[100];
                        }}
                      >
                        {t.needHelp}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIndex(1)}
                        className="text-xs font-medium"
                        style={{
                          borderRadius: radius.full,
                          background: colors.success.main,
                          color: colors.neutral.white,
                          padding: `${spacing[1]} ${spacing[3]}`,
                          transition: transitions.default,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = colors.success.dark;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = colors.success.main;
                        }}
                      >
                        {t.arrived}
                      </button>
                    </div>
                    {showNavigationHelp && (
                      <NavigationHelp
                        targetCoordinates={targetCoordinates}
                        locationName={step.title}
                        onClose={() => setShowNavigationHelp(false)}
                        lang={lang}
                      />
                    )}
                  </>
                )}
                {idx === 1 && !isMissionMode && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold">{t.headers.riddle}</h3>
                      <div className="flex items-center gap-2">
                        {step.answerMeta && !isCorrect && (
                          <span
                            className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                              background: 'rgba(107, 114, 128, 0.1)',
                              color: '#6B7280',
                              border: '1px solid rgba(107, 114, 128, 0.2)'
                            }}
                          >
                            {step.answerMeta}
                          </span>
                        )}
                        {isCorrect && (
                        <button
                          onClick={() => setIndex(2)}
                          className="text-xs font-medium"
                          style={{
                            borderRadius: radius.full,
                            background: colors.success.main,
                            color: colors.neutral.white,
                            padding: `${spacing[1]} ${spacing[3]}`,
                            transition: transitions.default,
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = colors.success.dark;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = colors.success.main;
                          }}
                        >
                          {t.learnAbout}
                        </button>
                        )}
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none text-slate-800 break-words">
                      <div className="whitespace-normal" dangerouslySetInnerHTML={{ __html: step.riddle }} />
                    </div>
                    <div className="mt-4">
                      {!isRevealed(step.id,"hint") ? (
                        <button
                          className="w-full text-sm font-semibold text-black justify-center text-center flex items-center gap-2 relative overflow-hidden"
                          style={{
                            borderRadius: radius.lg,
                            background: `linear-gradient(135deg, ${colors.treasure.gold} 0%, ${colors.treasure.amber} 100%)`,
                            padding: `${spacing[3]} ${spacing[4]}`,
                            transition: transitions.default,
                            boxShadow: `0 4px 12px rgba(218, 165, 32, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)`,
                            border: `1px solid ${colors.treasure.gold}`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(218, 165, 32, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(218, 165, 32, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                          }}
                          onClick={()=>{
                            try {
                              const unlockAt = (typeof window !== 'undefined' && window.__hintUnlockTimes) ? window.__hintUnlockTimes[step.id] : null;
                              const now = Date.now();
                              if(!unlockAt || now < unlockAt){ alert(t.hintWait); return; }
                            } catch {}
                            // Haptic feedback
                            if (navigator.vibrate) navigator.vibrate(50);
                            setJustUnlocked(true);
                            setTimeout(() => setJustUnlocked(false), 500);
                            reveal(step.id,"hint");
                          }}
                        >
                          <span>{t.clickForHint}</span>
                        </button>
                      ) : (
                        <div className={`mt-2 ${justUnlocked ? 'treasure-reveal' : ''}`}>
                          <div
                            className="relative p-4"
                            style={{
                              borderRadius: radius.lg,
                              background: isMissionMode
                                ? 'rgba(16, 185, 129, 0.1)'
                                : `linear-gradient(135deg, ${colors.treasure.gold}15 0%, ${colors.treasure.amber}15 100%)`,
                              border: isMissionMode
                                ? '2px solid rgba(16, 185, 129, 0.3)'
                                : `2px solid ${colors.treasure.gold}`,
                              boxShadow: isMissionMode
                                ? '0 8px 24px rgba(16, 185, 129, 0.2)'
                                : `0 8px 24px rgba(218, 165, 32, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)`,
                            }}
                          >
                            {/* Shine effect - only for non-mission mode */}
                            {!isMissionMode && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '50%',
                                  background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                                  borderRadius: `${radius.lg} ${radius.lg} 0 0`,
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                            <div className="flex items-start gap-3">
                              <span style={{ fontSize: '1.5em', flexShrink: 0 }}>
                                {isMissionMode ? '📡' : '🔓'}
                              </span>
                              <div className="flex-1">
                                <div
                                  className="text-xs font-semibold uppercase tracking-wider mb-2"
                                  style={{ color: isMissionMode ? '#10B981' : colors.treasure.gold }}
                                >
                                  {isMissionMode ? t.transmission : t.hintUnlocked}
                                </div>
                                <div
                                  className="text-sm font-medium"
                                  style={{ color: isMissionMode ? '#1F2937' : colors.neutral[800] }}
                                >
                                  {typedHint}
                                  {typedHint.length < (step.hint?.length || 0) && (
                                    <span style={{ animation: 'blink 1s infinite' }}>▋</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-5">
                      <AnswerBox
                        key={step.id}
                        step={step}
                        isCorrect={isCorrect}
                        onSubmit={onSubmit}
                        embedded
                        letterMask={letterMask}
                        onRevealLetter={onRevealLetter}
                        showRevealLetter={isRevealed(step.id,"hint")}
                        lang={lang}
                      />
                    </div>

                  </>
                )}
                {idx === 1 && isMissionMode && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold">Intel</h3>
                    </div>
                    {step.audioFile && (
                      <>
                        <div
                          className="mb-3 p-2 rounded-lg"
                          style={{
                            background: colors.neutral[50],
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          {/* Audio Controls */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={toggleAudio}
                              className="flex items-center justify-center shrink-0"
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '4px',
                                background: isAudioPlaying ? colors.success.main : colors.neutral[700],
                                color: colors.neutral.white,
                                border: 'none',
                                cursor: 'pointer',
                                transition: transitions.default,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              aria-label={isAudioPlaying ? 'Pause audio' : 'Play audio'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                {isAudioPlaying ? (
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                ) : (
                                  <path d="M8 5v14l11-7z" />
                                )}
                              </svg>
                            </button>
                            <div className="flex-1">
                              {/* Progress Bar */}
                              <div
                                onClick={handleSeek}
                                className="relative h-1.5 rounded-full cursor-pointer"
                                style={{
                                  background: colors.neutral[200],
                                }}
                              >
                                <div
                                  className="absolute top-0 left-0 h-full rounded-full transition-all"
                                  style={{
                                    width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
                                    background: colors.success.main,
                                  }}
                                />
                              </div>
                            </div>
                            {/* Time Display */}
                            <div className="text-xs font-mono shrink-0" style={{ color: colors.neutral[600] }}>
                              {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                            </div>
                          </div>
                        </div>
                        <audio
                          ref={audioRef}
                          src={step.audioFile}
                          onEnded={handleAudioEnded}
                          onTimeUpdate={handleTimeUpdate}
                          onLoadedMetadata={handleLoadedMetadata}
                          preload="metadata"
                        />
                      </>
                    )}
                    <div
                      ref={historyTextRef}
                      className="prose prose-sm max-w-none text-slate-800 break-words overflow-y-auto"
                      style={{
                        maxHeight: '200px',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      <div className="whitespace-normal" dangerouslySetInnerHTML={{ __html: step.history }} />
                    </div>
                  </>
                )}
                {idx === 2 && isMissionMode && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold">Cipher</h3>
                      <div className="flex items-center gap-2">
                        {step.answerMeta && !isCorrect && (
                          <span
                            className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                              background: 'rgba(107, 114, 128, 0.1)',
                              color: '#6B7280',
                              border: '1px solid rgba(107, 114, 128, 0.2)'
                            }}
                          >
                            {step.answerMeta}
                          </span>
                        )}
                        {isCorrect && (
                        <button
                          onClick={() => setIndex(1)}
                          className="text-xs font-medium"
                          style={{
                            borderRadius: radius.full,
                            background: colors.success.main,
                            color: colors.neutral.white,
                            padding: `${spacing[1]} ${spacing[3]}`,
                            transition: transitions.default,
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = colors.success.dark;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = colors.success.main;
                          }}
                        >
                          Read Intel
                        </button>
                        )}
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none text-slate-800 break-words">
                      <div className="whitespace-normal" dangerouslySetInnerHTML={{ __html: step.riddle }} />
                    </div>
                    {/* Show Spy Journal for final step of attribute collection tours (Chicago) */}
                    {isFinalStep && tour?.collectionMode === "attributes" && progressHook && (
                      <div className="mt-4">
                        <SpyJournalModal
                          tour={tour}
                          progressHook={progressHook}
                          embedded={true}
                        />
                      </div>
                    )}
                    <div className="mt-4">
                      {!isRevealed(step.id,"hint") ? (
                        <button
                          className="w-full text-sm font-semibold text-black justify-center text-center flex items-center gap-2 relative overflow-hidden"
                          style={{
                            borderRadius: radius.lg,
                            background: `linear-gradient(135deg, ${colors.treasure.gold} 0%, ${colors.treasure.amber} 100%)`,
                            padding: `${spacing[3]} ${spacing[4]}`,
                            transition: transitions.default,
                            boxShadow: `0 4px 12px rgba(218, 165, 32, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)`,
                            border: `1px solid ${colors.treasure.gold}`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(218, 165, 32, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(218, 165, 32, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                          }}
                          onClick={()=>{
                            try {
                              const unlockAt = (typeof window !== 'undefined' && window.__hintUnlockTimes) ? window.__hintUnlockTimes[step.id] : null;
                              const now = Date.now();
                              if(!unlockAt || now < unlockAt){ alert(t.hintWait); return; }
                            } catch {}
                            // Haptic feedback
                            if (navigator.vibrate) navigator.vibrate(50);
                            setJustUnlocked(true);
                            setTimeout(() => setJustUnlocked(false), 500);
                            reveal(step.id,"hint");
                          }}
                        >
                          <span>{t.clickForHint}</span>
                        </button>
                      ) : (
                        <div className={`mt-2 ${justUnlocked ? 'treasure-reveal' : ''}`}>
                          <div
                            className="relative p-4"
                            style={{
                              borderRadius: radius.lg,
                              background: isMissionMode
                                ? 'rgba(16, 185, 129, 0.1)'
                                : `linear-gradient(135deg, ${colors.treasure.gold}15 0%, ${colors.treasure.amber}15 100%)`,
                              border: isMissionMode
                                ? '2px solid rgba(16, 185, 129, 0.3)'
                                : `2px solid ${colors.treasure.gold}`,
                              boxShadow: isMissionMode
                                ? '0 8px 24px rgba(16, 185, 129, 0.2)'
                                : `0 8px 24px rgba(218, 165, 32, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)`,
                            }}
                          >
                            {/* Shine effect - only for non-mission mode */}
                            {!isMissionMode && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '50%',
                                  background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                                  borderRadius: `${radius.lg} ${radius.lg} 0 0`,
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                            <div className="flex items-start gap-3">
                              <span style={{ fontSize: '1.5em', flexShrink: 0 }}>
                                {isMissionMode ? '📡' : '🔓'}
                              </span>
                              <div className="flex-1">
                                <div
                                  className="text-xs font-semibold uppercase tracking-wider mb-2"
                                  style={{ color: isMissionMode ? '#10B981' : colors.treasure.gold }}
                                >
                                  {isMissionMode ? t.transmission : t.hintUnlocked}
                                </div>
                                <div
                                  className="text-sm font-medium"
                                  style={{ color: isMissionMode ? '#1F2937' : colors.neutral[800] }}
                                >
                                  {typedHint}
                                  {typedHint.length < (step.hint?.length || 0) && (
                                    <span style={{ animation: 'blink 1s infinite' }}>▋</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-5">
                      <AnswerBox
                        key={step.id}
                        step={step}
                        isCorrect={isCorrect}
                        onSubmit={onSubmit}
                        embedded
                        letterMask={letterMask}
                        onRevealLetter={onRevealLetter}
                        showRevealLetter={isRevealed(step.id,"hint")}
                        lang={lang}
                      />
                    </div>
                  </>
                )}
                {idx === 2 && !isMissionMode && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold">{t.headers.history}</h3>
                    </div>
                    {step.audioFile && (
                      <>
                        <div
                          className="mb-3 p-2 rounded-lg"
                          style={{
                            background: colors.neutral[50],
                            border: `1px solid ${colors.neutral[200]}`,
                          }}
                        >
                          {/* Audio Controls */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={toggleAudio}
                              className="flex items-center justify-center shrink-0"
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '4px',
                                background: isAudioPlaying ? colors.success.main : colors.neutral[700],
                                color: colors.neutral.white,
                                border: 'none',
                                cursor: 'pointer',
                                transition: transitions.default,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              aria-label={isAudioPlaying ? 'Pause audio' : 'Play audio'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                {isAudioPlaying ? (
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                ) : (
                                  <path d="M8 5v14l11-7z" />
                                )}
                              </svg>
                            </button>
                            <div className="flex-1">
                              {/* Progress Bar */}
                              <div
                                onClick={handleSeek}
                                className="relative h-1.5 rounded-full cursor-pointer"
                                style={{
                                  background: colors.neutral[200],
                                }}
                              >
                                <div
                                  className="absolute top-0 left-0 h-full rounded-full transition-all"
                                  style={{
                                    width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
                                    background: colors.success.main,
                                  }}
                                />
                              </div>
                            </div>
                            {/* Time Display */}
                            <div className="text-xs font-mono shrink-0" style={{ color: colors.neutral[600] }}>
                              {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                            </div>
                          </div>
                        </div>
                        <audio
                          ref={audioRef}
                          src={step.audioFile}
                          onEnded={handleAudioEnded}
                          onTimeUpdate={handleTimeUpdate}
                          onLoadedMetadata={handleLoadedMetadata}
                          preload="metadata"
                        />
                      </>
                    )}
                    <div
                      ref={historyTextRef}
                      className="prose prose-sm max-w-none text-slate-800 break-words overflow-y-auto"
                      style={{
                        maxHeight: '200px',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      <div className="whitespace-normal" dangerouslySetInnerHTML={{ __html: step.history }} />
                    </div>
                  </>
                )}
                {idx === 3 && hasNearbyRecommendations && !isMissionMode && (
                  <>
                    <h3 className="text-lg font-bold mb-2">{t.headers.nearby}</h3>
                    <LocalRecommendations step={step} limit={2} lang={lang} />
                  </>
                )}
              </Card>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
